import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { getEnvVar } from '../config/env';
import type { ApiResponse, PaginatedResponse, SystemMetrics, SystemStats, WorkflowExecution, IntegrationStatus, AdminAction, AdminWorkspaceSubscriptionRow, SubscriptionUpdatePayload, WorkspaceSubscriptionPricingPayload } from '../types';

const API_BASE_URL = getEnvVar('VITE_FASTAPI_BASE_URL');

class FastAPIService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: `${API_BASE_URL}/api/v1`,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Setup interceptors
    this.setupInterceptors();
  }

  // Add auth interceptor
  private setupInterceptors() {
    // Request interceptor to add auth token
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('admin_auth_token') || 
                     document.cookie
                       .split('; ')
                       .find(row => row.startsWith('auth_token='))
                       ?.split('=')[1];
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor for error handling
    this.api.interceptors.response.use(
      (response: AxiosResponse) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Clear auth token and redirect to login
          localStorage.removeItem('admin_auth_token');
          document.cookie = 'auth_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
          window.location.href = '/admin/login';
        }
        return Promise.reject(error);
      }
    );
  }

  // Generic API methods
  private async get<T = any>(url: string, params?: any): Promise<ApiResponse<T>> {
    const response = await this.api.get<ApiResponse<T>>(url, { params });
    return response.data;
  }

  private async post<T = any>(url: string, data?: any): Promise<ApiResponse<T>> {
    const response = await this.api.post<ApiResponse<T>>(url, data);
    return response.data;
  }

  private async put<T = any>(url: string, data?: any): Promise<ApiResponse<T>> {
    const response = await this.api.put<ApiResponse<T>>(url, data);
    return response.data;
  }

  private async patch<T = any>(url: string, data?: any): Promise<ApiResponse<T>> {
    const response = await this.api.patch<ApiResponse<T>>(url, data);
    return response.data;
  }

  private async delete<T = any>(url: string): Promise<ApiResponse<T>> {
    const response = await this.api.delete<ApiResponse<T>>(url);
    return response.data;
  }

  // Generic request method for flexible API calls
  async request<T = any>(
    url: string,
    options?: {
      method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
      body?: any;
      params?: any;
      timeout?: number;
    }
  ): Promise<ApiResponse<T>> {
    const method = (options?.method || 'GET').toUpperCase();
    const { body, params, timeout } = options || {};
    const config = timeout ? { timeout } : {};

    try {
      switch (method) {
        case 'GET':
          return await this.api.get<ApiResponse<T>>(url, { params, ...config }).then(r => r.data);
        case 'POST':
          return await this.api.post<ApiResponse<T>>(url, body, config).then(r => r.data);
        case 'PUT':
          return await this.api.put<ApiResponse<T>>(url, body, config).then(r => r.data);
        case 'PATCH':
          return await this.api.patch<ApiResponse<T>>(url, body, config).then(r => r.data);
        case 'DELETE':
          return await this.api.delete<ApiResponse<T>>(url, config).then(r => r.data);
        default:
          throw new Error(`Unsupported HTTP method: ${method}`);
      }
    } catch (error: any) {
      // Handle axios errors
      if (error.response) {
        throw {
          success: false,
          error: error.response.data?.detail || error.response.data?.error || error.message,
          data: null
        };
      }
      throw {
        success: false,
        error: error.message || 'Request failed',
        data: null
      };
    }
  }

  // Paginated requests
  private async getPaginated<T = any>(
    url: string, 
    params?: any
  ): Promise<PaginatedResponse<T>> {
    const response = await this.api.get<PaginatedResponse<T>>(url, { params });
    return response.data;
  }

  // Dashboard data
  async getDashboardData() {
    return this.get<any>('/admin/dashboard');
  }

  // System metrics
  async getSystemMetrics(): Promise<SystemMetrics> {
    try {
      const response = await this.get<SystemMetrics>('/admin/system-metrics');
      return response.data || this.getDefaultMetrics();
    } catch (error) {
      console.error('Error fetching system metrics:', error);
      return this.getDefaultMetrics();
    }
  }

  // System stats
  async getSystemStats(): Promise<SystemStats> {
    try {
      const response = await this.get<SystemStats>('/admin/system-stats');
      return response.data || this.getDefaultStats();
    } catch (error) {
      console.error('Error fetching system stats:', error);
      return this.getDefaultStats();
    }
  }

  // System health
  async getSystemHealth(): Promise<any> {
    try {
      const response = await this.get('/admin/health');
      return response.data || {};
    } catch (error) {
      console.error('Error fetching system health:', error);
      return {};
    }
  }

  // Queue statistics
  async getQueueStats(): Promise<any> {
    try {
      const response = await this.get('/admin/queues');
      return response.data || {};
    } catch (error) {
      console.error('Error fetching queue stats:', error);
      return {};
    }
  }

  // FastAPI logs (for admin monitoring)
  async getFastApiLogs(lines = 100, errorsOnly = false): Promise<{ logs: string[]; file: string; showing: number }> {
    try {
      const response = await this.get<{ logs: string[]; file: string; showing: number }>('/admin/monitoring/logs', {
        lines,
        errors_only: errorsOnly,
      });
      return response.data || { logs: [], file: '', showing: 0 };
    } catch (error) {
      console.error('Error fetching FastAPI logs:', error);
      return { logs: [], file: '', showing: 0 };
    }
  }

  async deleteFastApiLogs(): Promise<{ success: boolean; message?: string; error?: string }> {
    try {
      const response = await this.delete<{ success: boolean; message?: string; error?: string }>('/admin/monitoring/logs');
      return response.data || { success: false };
    } catch (error) {
      console.error('Error deleting FastAPI logs:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Failed to delete logs' };
    }
  }

  /** Prometheus text exposition (includes sws_copilot_* metrics). */
  async getAdminPrometheusText(): Promise<string> {
    const response = await this.api.get<string>('/admin/monitoring/prometheus', { responseType: 'text' });
    return typeof response.data === 'string' ? response.data : String(response.data ?? '');
  }

  // Workflow executions
  async getWorkflowExecutions(params?: {
    page?: number;
    limit?: number;
    status?: string;
    workspaceId?: string;
  }): Promise<{ data: WorkflowExecution[]; pagination: any }> {
    try {
      const response = await this.getPaginated<WorkflowExecution>('/admin/workflow-executions', params);
      return {
        data: response.data || [],
        pagination: response.pagination || { page: 1, limit: 20, total: 0 }
      };
    } catch (error) {
      console.error('Error fetching workflow executions:', error);
      return { data: [], pagination: { page: 1, limit: 20, total: 0 } };
    }
  }

  // Integration status
  async getIntegrationStatus(): Promise<IntegrationStatus> {
    try {
      const response = await this.get<IntegrationStatus>('/admin/integrations/status');
      return response.data || this.getDefaultIntegrationStatus();
    } catch (error) {
      console.error('Error fetching integration status:', error);
      return this.getDefaultIntegrationStatus();
    }
  }

  // Users list
  async getUsers(params?: { page?: number; limit?: number; search?: string; status?: string; role?: string }) {
    return this.getPaginated<any>('/admin/users', params);
  }

  // User detail
  async getUserDetail(userId: string) {
    return this.get<any>(`/admin/users/${userId}`);
  }

  // Delete user
  async deleteUser(userId: string) {
    return this.delete<any>(`/admin/users/${userId}`);
  }

  private getAdminBearer(): string | null {
    const raw =
      localStorage.getItem('admin_auth_token') ||
      document.cookie
        .split('; ')
        .find((row) => row.startsWith('auth_token='))
        ?.split('=')[1];
    if (!raw) return null;
    return raw.startsWith('Bearer ') ? raw : `Bearer ${raw}`;
  }

  /**
   * Streamed delete with SSE progress events (type: step | purge_summary | complete | fatal).
   */
  async deleteUserWithProgress(
    userId: string,
    onEvent: (data: Record<string, unknown>) => void
  ): Promise<void> {
    const auth = this.getAdminBearer();
    if (!auth) {
      throw { success: false, error: 'Not authenticated', data: null };
    }

    const url = `${API_BASE_URL}/api/v1/admin/users/${encodeURIComponent(userId)}/delete-stream`;
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: auth,
        Accept: 'text/event-stream',
      },
    });

    if (res.status === 401) {
      localStorage.removeItem('admin_auth_token');
      document.cookie = 'auth_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
      window.location.href = '/admin/login';
      throw { success: false, error: 'Unauthorized', data: null };
    }

    if (!res.ok) {
      let detail = res.statusText;
      try {
        const j = await res.json();
        detail = String(
          (j as { detail?: string; message?: string }).detail ||
            (j as { message?: string }).message ||
            detail
        );
      } catch {
        /* ignore */
      }
      throw { success: false, error: detail, data: null };
    }

    const reader = res.body?.getReader();
    if (!reader) {
      throw { success: false, error: 'No response body', data: null };
    }

    const decoder = new TextDecoder();
    let buffer = '';
    let sawComplete = false;
    let fatalError: string | null = null;

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const chunks = buffer.split('\n\n');
      buffer = chunks.pop() ?? '';
      for (const chunk of chunks) {
        const line = chunk.split('\n').find((ln) => ln.startsWith('data: '));
        if (!line) continue;
        const raw = line.slice(6).trim();
        if (!raw) continue;
        try {
          const data = JSON.parse(raw) as Record<string, unknown>;
          onEvent(data);
          if (data.type === 'complete') sawComplete = true;
          if (data.type === 'fatal') {
            fatalError = String(data.error ?? 'Delete failed');
          }
        } catch {
          /* ignore malformed chunk */
        }
      }
    }

    if (fatalError) {
      throw { success: false, error: fatalError, data: null };
    }
    if (!sawComplete) {
      throw { success: false, error: 'Delete finished without confirmation from server', data: null };
    }
  }

  // User workspaces
  async getUserWorkspaces(userId: string, params?: { page?: number; limit?: number }) {
    return this.getPaginated<any>(`/admin/users/${userId}/workspaces`, params);
  }

  // User invoices
  async getUserInvoices(userId: string, params?: { page?: number; limit?: number; status?: string }) {
    return this.getPaginated<any>(`/admin/users/${userId}/invoices`, params);
  }

  // User subscriptions
  async getUserSubscriptions(userId: string) {
    return this.get<any>(`/admin/users/${userId}/subscriptions`);
  }

  // Admin: Enroll user in plan
  async enrollUserInPlan(userId: string, workspaceId: string, planName: string) {
    return this.post<any>(`/admin/users/${userId}/subscriptions`, { workspace_id: workspaceId, plan_name: planName });
  }

  // Admin: Change subscription plan (single subscription - legacy)
  async changeUserSubscriptionPlan(userId: string, subscriptionId: string, planName: string) {
    return this.patch<any>(`/admin/users/${userId}/subscriptions/${subscriptionId}/plan`, { plan_name: planName });
  }

  // Admin: Change user's plan as a whole (updates all workspace subscriptions for this user)
  async changeUserPlan(userId: string, planName: string) {
    return this.patch<any>(`/admin/users/${userId}/subscription-plan`, { plan_name: planName });
  }

  // Admin: workspace subscription directory
  async getSubscriptions(params?: {
    page?: number;
    limit?: number;
    search?: string;
    tab?: 'active' | 'expired' | 'trial';
    computed_state?: string;
    plan_name?: string;
    has_override?: boolean;
  }) {
    return this.getPaginated<AdminWorkspaceSubscriptionRow>('/admin/subscriptions', params);
  }

  async updateSubscription(subscriptionId: string, body: SubscriptionUpdatePayload) {
    return this.patch<any>(`/admin/subscriptions/${subscriptionId}`, body);
  }

  async updateWorkspaceSubscriptionPricing(workspaceId: string, body: WorkspaceSubscriptionPricingPayload) {
    return this.put<any>(`/admin/pricing/workspaces/${workspaceId}`, body);
  }

  // Workspaces list
  async getWorkspaces(params?: { page?: number; limit?: number; search?: string; status?: string }) {
    return this.getPaginated<any>('/admin/workspaces', params);
  }

  /** Enterprise admin: aggregated workspace 360° (members, credits, domains, workflows) */
  async getWorkspaceAdminOverview(workspaceId: string) {
    return this.get<{ success: boolean; data: Record<string, unknown> }>(
      `/admin/workspaces/${encodeURIComponent(workspaceId)}/overview`
    );
  }

  /** Admin: call logs / recordings for a workspace (same payload shape as CRM Call Logs). */
  async getWorkspaceCallRecordings(
    workspaceId: string,
    params?: { limit?: number; offset?: number; direction?: string }
  ) {
    return this.get<{ success: boolean; data: Record<string, unknown>[]; total: number }>(
      `/admin/workspaces/${encodeURIComponent(workspaceId)}/call-recordings`,
      params
    );
  }

  /** Enterprise admin: cross-tenant verified / sending domains */
  async getAdminVerifiedDomains(params?: {
    page?: number;
    limit?: number;
    search?: string;
    verification_status?: string;
    workspace_id?: string;
  }) {
    return this.getPaginated<any>('/admin/verified-domains', params);
  }

  // Workflow metrics (per-workspace)
  async getWorkflowMetrics(workspaceId: string) {
    const response = await this.get<any>('/admin/workflow-metrics', { workspace_id: workspaceId });
    return response?.data ?? response;
  }

  // Dead-letter queue list
  async getWorkflowDeadLetter(workspaceId?: string) {
    const params = workspaceId ? { workspace_id: workspaceId } : {};
    const response = await this.get<any[]>('/admin/workflows/dead-letter', params);
    return Array.isArray(response) ? response : (response?.data ?? response ?? []);
  }

  // Redrive DLQ execution
  async redriveWorkflowExecution(executionId: string) {
    return this.post<any>(`/admin/workflows/redrive/${executionId}`);
  }

  // Workspace email provider (Resend or Mailgun)
  async getWorkspaceEmailProvider(workspaceId: string) {
    return this.get<any>(`/admin/workspaces/${workspaceId}/email-provider`);
  }

  async updateWorkspaceEmailProvider(workspaceId: string, provider: 'resend' | 'mailgun') {
    return this.put<any>(`/admin/workspaces/${workspaceId}/email-provider?provider=${provider}`);
  }

  async getNotificationSenders() {
    return this.get<any>('/admin/notification-senders');
  }

  async updateNotificationSenders(payload: {
    indian_default_e164?: string;
    international_default_e164?: string;
    voice_enabled?: boolean;
    sms_enabled?: boolean;
    notification_voice_credits?: number;
  }) {
    return this.put<any>('/admin/notification-senders', payload);
  }

  async getFirecrawlConfig() {
    return this.get<any>('/admin/firecrawl-config');
  }

  async updateFirecrawlConfig(payload: { base_url: string }) {
    return this.put<any>('/admin/firecrawl-config', payload);
  }

  async getSearxngConfig() {
    return this.get<any>('/admin/searxng-config');
  }

  async updateSearxngConfig(payload: { base_url: string }) {
    return this.put<any>('/admin/searxng-config', payload);
  }

  /** Get full email config status (Mailgun key, provider, verified domains) for verification */
  async getWorkspaceEmailConfigStatus(workspaceId: string) {
    return this.get<any>(`/admin/workspaces/${workspaceId}/email-config-status`);
  }

  // Workspace credits (super admin)
  async getWorkspaceCredits(workspaceId: string) {
    return this.get<any>(`/admin/workspaces/${workspaceId}/credits`);
  }

  async addWorkspaceCredits(workspaceId: string, amount: number, description?: string) {
    return this.post<any>(`/admin/workspaces/${workspaceId}/credits/add`, { amount, description });
  }

  // Invoices list
  async getInvoices(params?: { page?: number; limit?: number; search?: string; status?: string; workspace_id?: string; user_id?: string }) {
    return this.getPaginated<any>('/admin/invoices', params);
  }

  /** Notification/email delivery history for an invoice (admin audit). */
  async getInvoiceNotificationLogs(invoiceId: string) {
    return this.get<any[]>(`/admin/invoices/${encodeURIComponent(invoiceId)}/notification-logs`);
  }

  /** Force re-render from latest template config (no email, no persistence). */
  async regenerateAdminInvoicePdf(invoiceId: string) {
    return this.post<{ pdf_path?: string; filename?: string; regenerated?: boolean }>(
      `/admin/invoices/${encodeURIComponent(invoiceId)}/regenerate-pdf`
    );
  }

  /** Resend payment-confirmation invoice email with PDF to the invoice customer (admin only). */
  async resendAdminInvoiceEmail(invoiceId: string, params?: { email?: string }) {
    const path = `/admin/invoices/${encodeURIComponent(invoiceId)}/resend-email`;
    const qs = params?.email ? `?email=${encodeURIComponent(params.email)}` : '';
    const response = await this.api.post<ApiResponse<{ message?: string; email?: string; invoice_id?: string }>>(
      `${path}${qs}`,
    );
    return response.data;
  }

  /** Open/download invoice PDF named by invoice number (e.g. ET-26-SA-0007.pdf). */
  async openAdminInvoicePdfInNewTab(invoiceId: string): Promise<void> {
    const path = `/admin/invoices/${encodeURIComponent(invoiceId)}/pdf-file`;
    const response = await this.api.get(path, { responseType: 'blob' });
    const blob = response.data as Blob;
    const headerName =
      (response.headers?.['x-invoice-filename'] as string | undefined) ||
      (response.headers?.['X-Invoice-Filename'] as string | undefined) ||
      '';
    let filename = (headerName || '').trim();
    if (!filename) {
      const cd = String(response.headers?.['content-disposition'] || '');
      const mStar = /filename\*=UTF-8''([^;]+)/i.exec(cd);
      const mPlain = /filename="?([^";]+)"?/i.exec(cd);
      if (mStar?.[1]) {
        try {
          filename = decodeURIComponent(mStar[1]);
        } catch {
          filename = mStar[1];
        }
      } else if (mPlain?.[1]) {
        filename = mPlain[1];
      }
    }
    if (!filename) filename = `invoice-${invoiceId}.pdf`;
    if (!filename.toLowerCase().endsWith('.pdf')) filename = `${filename}.pdf`;

    const url = window.URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
    // Also try opening a named view tab (blob URLs still show a UUID in the address bar)
    const viewWin = window.open(url, '_blank', 'noopener,noreferrer');
    if (!viewWin) {
      /* popup blocked — download still happened */
    }
    window.setTimeout(() => window.URL.revokeObjectURL(url), 120_000);
  }

  /** JSON metadata (filename, pdf_path) for admin invoice PDF. */
  async getAdminInvoicePdfMeta(invoiceId: string) {
    return this.get<{ filename?: string; pdf_path?: string; invoice?: unknown; message?: string }>(
      `/admin/invoices/${encodeURIComponent(invoiceId)}/pdf`
    );
  }

  // Invoice PDF templates (admin — platform-wide by default)
  async getInvoiceTemplates(params?: { workspace_id?: string; include_inactive?: boolean }) {
    return this.get<{ data: any[]; total: number }>('/admin/invoice-templates', params);
  }

  async getInvoiceTemplate(templateId: string) {
    return this.get<any>(`/admin/invoice-templates/${templateId}`);
  }

  async createInvoiceTemplate(body: Record<string, unknown>) {
    return this.post<any>('/admin/invoice-templates', body);
  }

  async updateInvoiceTemplate(templateId: string, body: Record<string, unknown>) {
    return this.patch<any>(`/admin/invoice-templates/${templateId}`, body);
  }

  async deleteInvoiceTemplate(templateId: string) {
    return this.delete<any>(`/admin/invoice-templates/${templateId}`);
  }

  async setDefaultInvoiceTemplate(templateId: string) {
    return this.post<any>(`/admin/invoice-templates/${templateId}/set-default`);
  }

  /** Upload image to GCS under Admin/invoice-templates/... (admin auth). */
  async uploadAdminAssetImage(
    file: File,
    purpose: 'invoice_header' | 'invoice_footer' | 'invoice_page_background',
  ): Promise<{ url: string; path?: string; content_type?: string }> {
    const form = new FormData();
    form.append('file', file, file.name);
    form.append('purpose', purpose);
    try {
      const response = await this.api.post<{ url: string; path?: string; content_type?: string }>(
        '/admin/assets/upload-image',
        form,
        {
          // Instance default is application/json — that breaks multipart and FastAPI returns 422.
          transformRequest: [
            (data, headers) => {
              if (data instanceof FormData) {
                if (headers && typeof (headers as any).delete === 'function') {
                  (headers as any).delete('Content-Type');
                } else if (headers && typeof headers === 'object') {
                  delete (headers as Record<string, unknown>)['Content-Type'];
                }
              }
              return data;
            },
          ],
          timeout: 120000,
          maxBodyLength: Infinity,
          maxContentLength: Infinity,
        },
      );
      return response.data;
    } catch (error: unknown) {
      const ax = error as { response?: { data?: { detail?: unknown } } };
      const detail = ax.response?.data?.detail;
      let msg = 'Upload failed';
      if (typeof detail === 'string') msg = detail;
      else if (Array.isArray(detail)) {
        msg = detail.map((e: { msg?: string }) => e.msg || JSON.stringify(e)).join('; ');
      }
      throw new Error(msg || 'Upload failed');
    }
  }

  // Workflows list
  async getWorkflows(params?: { page?: number; limit?: number; search?: string; status?: string }) {
    return this.getPaginated<any>('/admin/workflows', params);
  }

  // Workflow runs
  async getWorkflowRuns(params?: { page?: number; limit?: number; status?: string }) {
    return this.getPaginated<any>('/admin/workflow-runs', params);
  }

  // Platform (super) admin audit log — GET /admin/actions
  async getRecentActions(params?: {
    page?: number;
    limit?: number;
    /** Substring match on action */
    search?: string;
    actor_user_id?: string;
  }): Promise<{ data: AdminAction[]; pagination: any }> {
    try {
      const response = await this.getPaginated<AdminAction>('/admin/actions', params);
      return {
        data: (response as any).data || [],
        pagination: (response as any).pagination || { page: 1, limit: 20, total: 0 }
      };
    } catch (error) {
      console.error('Error fetching recent actions:', error);
      return { data: [], pagination: { page: 1, limit: 20, total: 0 } };
    }
  }

  // Default metrics for fallback
  private getDefaultMetrics(): SystemMetrics {
    return {
      users: {
        total: 0,
        active: 0,
        newThisMonth: 0
      },
      workspaces: {
        total: 0,
        active: 0,
        trial: 0
      },
      workflows: {
        total: 0,
        active: 0,
        executionsToday: 0
      },
      system: {
        cpuUsage: 0,
        memoryUsage: 0,
        diskUsage: 0,
        queueStatus: 'healthy'
      }
    };
  }

  // Default stats for fallback
  private getDefaultStats(): SystemStats {
    return {
      users: 0,
      workspaces: 0,
      workflows: 0,
      activeRuns: 0,
      redis: {
        connected_clients: 0,
        used_memory_human: '0B',
        uptime_in_seconds: 0,
      },
      queues: {},
    };
  }

  /** Jarvis / CRM tool console (admin) */
  async getJarvisTools(): Promise<{ success?: boolean; count?: number; tools: any[] }> {
    const r = (await this.get<any>('/admin/jarvis-tools')) as any;
    return { success: r?.success, count: r?.count, tools: r?.tools || [] };
  }

  async updateJarvisTool(
    toolName: string,
    patch: { display_name?: string; description?: string; enabled?: boolean; category?: string; notes?: string }
  ): Promise<any> {
    return this.patch<any>(`/admin/jarvis-tools/${encodeURIComponent(toolName)}`, patch);
  }

  async getJarvisToolSchema(toolName: string): Promise<any> {
    return this.get<any>(`/admin/jarvis-tools/${encodeURIComponent(toolName)}/schema`);
  }

  async testJarvisTool(
    toolName: string,
    body: {
      workspace_id: string;
      user_profile_id: string;
      payload?: Record<string, unknown>;
      dry_run?: boolean;
      session_id?: string;
    }
  ): Promise<any> {
    return this.post<any>(`/admin/jarvis-tools/${encodeURIComponent(toolName)}/test`, body);
  }

  async getToolsManifest(): Promise<any> {
    return this.get<any>('/tools/manifest');
  }

  async getToolsManifestOpenAI(): Promise<any> {
    return this.get<any>('/tools/manifest/openai');
  }

  async getToolsManifestMcp(): Promise<any> {
    return this.get<any>('/tools/manifest/mcp');
  }

  async getPortableCatalogFile(): Promise<any> {
    return this.get<any>('/tools/catalog/file');
  }

  // Default integration status for fallback
  private getDefaultIntegrationStatus(): IntegrationStatus {
    return {
      email: {
        gmail: { status: 'disabled', lastChecked: new Date().toISOString() },
        mailgun: { status: 'disabled', lastChecked: new Date().toISOString() },
        resend: { status: 'disabled', lastChecked: new Date().toISOString() }
      },
      whatsapp: {
        evolutionApi: { status: 'disabled', lastChecked: new Date().toISOString() },
        retell: { status: 'disabled', lastChecked: new Date().toISOString() }
      },
      payment: {
        razorpay: { status: 'disabled', lastChecked: new Date().toISOString() }
      }
    };
  }
}

export const fastapiService = new FastAPIService();
export default fastapiService;


